import { getMessageFileDownloadFilename, isFolderMessageFile } from './message-file-metadata';
import type { UIMessageWithMetadata } from '../../../lib/types';
import type { GeneratedFileListItem } from '../components/tools/generated-files-list';

interface GeneratedFilesShape {
  generatedFiles?: ReadonlyArray<{ fileUuid: string; filename: string }>;
}

interface GeneratedFileUuidsShape {
  generatedFileUuids?: ReadonlyArray<string>;
}

type ToolPartLike = NonNullable<UIMessageWithMetadata['parts']>[number] & {
  state?: string;
  output?: unknown;
};

function synthesizeFilename(toolName: string, index: number, total: number): string {
  if (toolName === 'fillSl1Form') {
    return total === 1 ? 'SL1 form.pdf' : `SL1 form ${index + 1}.pdf`;
  }
  if (toolName === 'fillSl2Form') {
    return total === 1 ? 'SL2 form.pdf' : `SL2 form ${index + 1}.pdf`;
  }
  return `Generated file ${index + 1}`;
}

function extractFromPart(part: ToolPartLike): GeneratedFileListItem[] {
  if (!part.type.startsWith('tool-') || part.state !== 'output-available') return [];
  const output = part.output;
  if (!output || typeof output !== 'object') return [];

  const toolName = part.type.replace('tool-', '');

  const namedFiles = (output as GeneratedFilesShape).generatedFiles;
  if (namedFiles && namedFiles.length > 0) {
    return namedFiles.map((file) => ({ uuid: file.fileUuid, filename: file.filename }));
  }

  const uuids = (output as GeneratedFileUuidsShape).generatedFileUuids;
  if (uuids && uuids.length > 0) {
    return uuids.map((uuid, idx) => ({ uuid, filename: synthesizeFilename(toolName, idx, uuids.length) }));
  }

  return [];
}

/**
 * Collects every generated file produced by tool parts on an assistant message into a
 * single deduplicated list. Used to hoist tool-generated downloads to a top-level slot
 * on the message instead of relying on per-part renderers — which get buried inside the
 * collapsed "How Nova got here" chain-of-thought block when reasoning is enabled.
 */
export function extractToolGeneratedFiles(message: UIMessageWithMetadata): GeneratedFileListItem[] {
  if (!message.parts) return [];
  const seen = new Set<string>();
  const result: GeneratedFileListItem[] = [];
  for (const part of message.parts) {
    for (const file of extractFromPart(part as ToolPartLike)) {
      const key = file.uuid ?? `pending-${result.length}`;
      if (seen.has(key)) continue;
      seen.add(key);
      result.push(file);
    }
  }
  return result;
}

export function getMessageAttachmentFiles(message: UIMessageWithMetadata) {
  const files = message.metadata?.files ?? [];
  return message.role === 'assistant' ? files.filter(isFolderMessageFile) : files;
}

export function extractAssistantMessageGeneratedFiles({
  existingGeneratedFiles = [],
  message,
}: {
  existingGeneratedFiles?: readonly GeneratedFileListItem[];
  message: UIMessageWithMetadata;
}): GeneratedFileListItem[] {
  if (message.role !== 'assistant') return [];

  const seen = new Set(existingGeneratedFiles.map((file) => file.uuid).filter((uuid): uuid is string => Boolean(uuid)));

  return (message.metadata?.files ?? [])
    .filter((file) => !isFolderMessageFile(file) && !seen.has(file.uuid))
    .map((file) => ({
      uuid: file.uuid,
      filename: getMessageFileDownloadFilename(file),
    }));
}
